/**
 * The app: a session state machine (loading → signed-out → no-org → ready)
 * around the pages. Everything with a side effect is injected (auth, API
 * transport, board connection), so the whole app runs in tests against
 * the real API in memory.
 */
import { useCallback, useEffect, useState } from 'react';
import { ApiError, type Me, type WebApi } from './api.js';
import type { AuthSession, AuthUser } from './auth.js';
import { KolaArea } from './kola-area.js';
import { CreditsPage } from './pages/Credits.js';
import { MachinesPage } from './pages/Machines.js';
import { MembersPage } from './pages/Members.js';
import { NoOrgPage } from './pages/NoOrg.js';
import { PricingPage, type PricingViewer } from './pages/Pricing.js';
import { ReviewPage } from './pages/Review.js';
import { SettingsPage, type Theme } from './pages/Settings.js';
import { SignInPage } from './pages/SignIn.js';
import { navigate, useRoute } from './router.js';
import { loadStripeJs, type LoadStripe } from './stripe.js';
import { AppNav, Note, type NavItem } from './ui/index.js';

export interface AppDeps { api: WebApi; auth: AuthSession; appUrl: string; pricingUrl: string; now?: () => number; /** Stripe.js, for the embedded Checkout; tests hand in a fake. */ loadStripe?: LoadStripe; /** Leave the app for another site (Stripe's hosted pages); tests watch it. */ openUrl?: (url: string) => void; }
type Session = { state: 'loading' } | { state: 'signed-out' } | { state: 'no-org'; user: AuthUser } | { state: 'ready'; user: AuthUser; me: Me } | { state: 'error'; user: AuthUser; message: string };

const NAV: NavItem[] = [{ id: 'boards', label: 'Boards', icon: 'layout-grid' }, { id: 'review', label: 'Review', icon: 'eye' }, { id: 'members', label: 'Members', icon: 'users' }, { id: 'machines', label: 'Machines', icon: 'monitor' }, { id: 'credits', label: 'Credits', icon: 'coins' }, { id: 'settings', label: 'Settings', icon: 'settings' }];

function readTheme(): Theme { try { const t = localStorage.getItem('rv-theme'); return t === 'light' ? 'light' : 'dark'; } catch { return 'dark'; } }

export function App(deps: AppDeps) {
  const { api, auth } = deps;
  const [session, setSession] = useState<Session>({ state: 'loading' });
  const [theme, setTheme] = useState<Theme>(readTheme);
  const route = useRoute();

  /** Load the org for the signed-in user; without org claims, ask the API to bootstrap them and refresh the token once. */
  const resolve = useCallback(async (user: AuthUser) => {
    try { setSession({ state: 'ready', user, me: await api.me() }); return; }
    catch (e) { if (!(e instanceof ApiError) || e.status !== 401) { setSession({ state: 'error', user, message: e instanceof Error ? e.message : String(e) }); return; } }
    try { await api.bootstrap(); await auth.idToken(true); setSession({ state: 'ready', user, me: await api.me() }); }
    catch (e) {
      if (e instanceof ApiError && (e.status === 404 || e.status === 401)) setSession({ state: 'no-org', user });
      else setSession({ state: 'error', user, message: e instanceof Error ? e.message : String(e) });
    }
  }, [api, auth]);

  useEffect(() => {
    let active = true;
    void auth.completeSignInLink().catch(() => false).finally(() => { if (!active) return; const u = auth.current(); if (u) void resolve(u); else setSession({ state: 'signed-out' }); });
    const off = auth.onChange((u) => { if (!active) return; if (u) void resolve(u); else setSession({ state: 'signed-out' }); });
    return () => { active = false; off(); };
  }, [auth, resolve]);

  useEffect(() => { document.documentElement.setAttribute('data-theme', theme); try { localStorage.setItem('rv-theme', theme); } catch { /* private mode */ } }, [theme]);

  const signOut = () => { void auth.signOut().then(() => navigate('/boards')); };
  const loadStripe = deps.loadStripe ?? loadStripeJs;
  const openUrl = deps.openUrl ?? ((url: string) => window.location.assign(url));

  // The price list is public: it shows from any state, with what the person can do from there.
  if (route.page === 'pricing' && session.state !== 'loading') {
    const viewer: PricingViewer = session.state === 'ready' ? { kind: 'member', role: session.me.me.role, plan: session.me.org.plan } : session.state === 'no-org' ? { kind: 'no-org' } : { kind: 'signed-out' };
    const startTrial = async () => { if (session.state !== 'no-org') return; await api.createOrg(); await auth.idToken(true); navigate('/boards'); await resolve(session.user); };
    const page = <PricingPage api={api} viewer={viewer} loadStripe={loadStripe} openUrl={openUrl} onSignIn={() => navigate('/')} onStartTrial={startTrial} />;
    if (session.state !== 'ready') return <main className="rv-public">{page}</main>;
    return <div className="rv-shell"><AppNav items={NAV} active="credits" onSelect={(id) => navigate(`/${id}`)} brand="Rainvic" className="rv-shell__nav" /><div className="rv-shell__main">{page}</div><AppNav items={NAV} active="credits" onSelect={(id) => navigate(`/${id}`)} layout="tabs" className="rv-shell__tabs" label="Main (tabs)" /></div>;
  }

  if (session.state === 'loading') return <main className="rv-center" aria-busy="true"><p className="rv-body rv-muted">Loading…</p></main>;
  if (session.state === 'signed-out') return <SignInPage auth={auth} />;
  if (session.state === 'no-org') return <NoOrgPage api={api} user={session.user} {...(route.page === 'invite' ? { initialCode: route.code } : {})} pricingUrl={deps.pricingUrl} onJoined={async () => { await auth.idToken(true); navigate('/boards'); await resolve(session.user); }} onSignOut={signOut} />;
  if (session.state === 'error') return <main className="rv-center"><Note tone="danger">{session.message}</Note><button type="button" className="rv-btn rv-btn--secondary rv-btn--md" onClick={() => void resolve(session.user)}><span>Try again</span></button></main>;

  const { me, user } = session;
  const active = route.page === 'kola' || route.page === 'invite' ? 'boards' : route.page;
  const page = (() => {
    switch (route.page) {
      case 'review': return <ReviewPage api={api} role={me.me.role} {...(route.boardId ? { boardId: route.boardId } : {})} onOpen={(id) => navigate(id ? `/review/${encodeURIComponent(id)}` : '/review')} />;
      case 'members': return <MembersPage api={api} me={me.me} appUrl={deps.appUrl} />;
      case 'machines': return <MachinesPage api={api} role={me.me.role} {...(deps.now ? { now: deps.now } : {})} />;
      case 'credits': return <CreditsPage api={api} me={me} pricingUrl={deps.pricingUrl} loadStripe={loadStripe} openUrl={openUrl} checkoutReturn={new URLSearchParams(window.location.search).has('checkout')} />;
      case 'settings': return <SettingsPage me={me} user={user} theme={theme} onTheme={setTheme} onSignOut={signOut} />;
      default: return <KolaArea auth={auth} theme={theme} path={route.page === 'kola' ? route.path : '/'} onPath={navigate} />;
    }
  })();
  return (
    <div className="rv-shell">
      <AppNav items={NAV} active={active} onSelect={(id) => navigate(`/${id}`)} brand="Rainvic" className="rv-shell__nav" />
      <div className={route.page === 'kola' ? 'rv-shell__main rv-shell__main--kola' : 'rv-shell__main'}>
        {me.org.status !== 'active' ? <Note tone="warning">{me.org.status === 'past_due' ? 'Payment is overdue: the workspace is read-only until it clears.' : 'This workspace is read-only.'}</Note> : null}
        {page}
      </div>
      <AppNav items={NAV} active={active} onSelect={(id) => navigate(`/${id}`)} layout="tabs" className="rv-shell__tabs" label="Main (tabs)" />
    </div>
  );
}
